// Forked from halifax (packages/cap1-components/src/TravelerSelectWorkflow/component.tsx)
// on 2026-06-15 so the experiences module can iterate on traveler-band UX without
// touching the shared component. Keep an eye on upstream halifax fixes — they will
// not flow into this copy automatically.

import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";
import React, { MouseEvent, useState } from "react";
import {
  ICorpPerson,
  ICorpPersonWithNameNumber,
  IPerson,
  ITravelerStepErrors,
  MODAL_ALERT,
  MODAL_ALERT_CHOICE,
  ModalButtonType,
  ModalCategoryType,
  PersonId,
  Tenant,
  UserInfo,
} from "redmond";
import { CORPORATE_ROLE_ADMIN, isCorpTenant } from "@capone/common";

import {
  ActionButton,
  ActionLink,
  BackButton,
  CloseButtonIcon,
  CorpGenericConfirmationModalContent,
  DesktopPopupModal,
  GenericInfoPopup,
  Icon,
  IconName,
  MobileFloatingButton,
  MobilePopoverCard,
  ToggleActions,
  TravelerInfoForm,
  TravelerSelectRow,
  TravelerSelectRowUpdated,
  UnsavedChangesModal,
  getDateTimeWithFormat,
  getPassengerName,
} from "halifax";

import "./styles.scss";

export enum TravelerSelectStep {
  Main,
  // eslint-disable-next-line no-shadow
  TravelerInfoForm,
  TravelerSelect,
  TravelerDeleteModal,
}

export type TravelerTypeTitles = {
  addTravelers?: string;
  additionalInfoTitle: string;
  adultTitle: string;
  childTitle: string;
  frequentFlyerTitle: string;
  frequentFlyerSubtitle?: string;
  infantLapTitle: string;
  infantSeatTitle: string;
  travelerInfoFormSubtitle?: string;
  travelerInfoSubtitle: string | React.ReactNode;
  travelerInfoTitle: string;
  travelerInfoSectionTitle?: string;
  passportTitle?: string;
  passportSubtitle?: string;
  passportSubtitle2?: string;
  noTravelersTitle?: string;
  completeTravelerProfileTitle?: string;
  completeTravelerProfileSubtitle?: string;
  editTravelerTitle?: string;
  editTravelerSubtitle?: string;
};

export type TravelerGroup = {
  /** Stable key for this group (e.g. an age-band enum value). */
  id: string;
  /** Header label rendered by the default header layout, e.g. "Adults". */
  label: string;
  /** Optional secondary header text rendered next to the label, e.g. "(Ages 18 – 64)". */
  sublabel?: string;
  /** IDs of travelers belonging to this group, used to partition the flat travelers list. */
  travelerIds: PersonId[];
  /** Required count for this group. Drives the "n / N selected" chip and red-state. */
  required: number;
  /**
   * Replaces the default header layout (label + sublabel + count chip) for this group.
   * The default still applies for groups that omit `customHeader`, so callers can mix-and-match.
   */
  customHeader?: React.ReactNode;
  isLast: boolean;
};

export interface ITravelerSelectWorkflowProps {
  buttonClassName?: string;
  buttonMessage?: string;
  className?: string;
  disableDriversLicenseValidation: boolean;
  disabled: boolean;
  disallowSelect: boolean;
  editPassenger?: IPerson;
  fullScreenWithBanner: boolean;
  handleDeletePassenger: (travelerId: PersonId) => void;
  handleUpdatePassenger: (
    traveler: IPerson,
    hideProfileAction: ToggleActions,
    entryPoint?: string,
    updatePassport?: boolean,
    isNewTraveler?: boolean,
  ) => void;
  isDialog: boolean;
  isFlights: boolean;
  isMobile: boolean;
  onContinue?: () => void;
  onGoBack?: () => void;
  onMobileBack?: () => void;
  onProgressChange?: (step: TravelerSelectStep) => void;
  progress: TravelerSelectStep;
  readOnly: boolean;
  saveButtonClicked: boolean;
  // TODO: figure out why selectedTravelerIds: Set<PersonId> gets converted to be an Object
  selectedTravelerIds: PersonId[];
  selectionScreenHeaderElement?: JSX.Element;
  showAdditionalInfoSection: boolean;
  showDriverLicenseSection: boolean;
  showErrors?: ITravelerStepErrors;
  showFrequentFlyerSection: boolean;
  showGenderField: boolean;
  showNationalityField: boolean;
  singleTravelerWorkflow: boolean;
  setProgress: (progress: TravelerSelectStep) => void;
  setSelectedTravelerIds: (selectedTravelerIds: PersonId[]) => void;
  handleSelectTraveler?: (travelerId: string) => void;
  setShowErrors?: (showErrors: ITravelerStepErrors) => void;
  titles: TravelerTypeTitles;
  travelers: IPerson[] | ICorpPerson[];
  userInfo?: UserInfo;
  wrapButton: boolean;
  bottomContent?: JSX.Element;
  showPassportSection?: boolean;
  requirePassport?: boolean;
  requireNationality?: boolean;
  // Add new traveler ActionButton instead of ActionLink
  addNewTravelerButton?: boolean;
  noHeadings?: boolean;
  updatedDesign?: boolean;
  allowRowClick?: boolean;
  travelerSelectContentOnly?: boolean;
  onClickAddNewTraveler?: () => void;
  onClickEditTraveler?: (traveler: IPerson) => void;
  onClickDeleteTraveler?: (traveler: IPerson) => void;
  userRoles: string[];
  tenant: Tenant;
  trackEvent: (event: {
    eventName: string;
    properties: Record<string, unknown>;
  }) => void;
  maxTravelerCount?: number;
  customHeader?: React.ReactNode;
  hideDelete?: boolean;
  showHotelLoyaltySection?: boolean;
  showGroundLoyaltySection?: boolean;
  hideTravelerActions?: boolean;
  onTravelerFormClose?: () => void;
  checkMissingInfoCompletion?: boolean;
  mobileTravelerRowType?: "button" | "checkbox";
  showMobilePopoverTransition?: boolean;
  showMobileChangeCTA?: boolean;
  bypassSelectForSingleTraveler?: boolean;
  showNonModalContent?: boolean;
  renderCustomAdditionalInfoSection?: (errorOnSave: boolean) => React.ReactNode;
  hideMobileContinueButton?: boolean;
  hideBackButton?: boolean;
  mobileContinueButtonText?: string;
  hideTravelerType?: boolean;
  getTravelerTypeByAge?: (age: number) => string | undefined;
  useXmodHeadings?: boolean;
  isValidatingPassenger?: (travelerId: string) => boolean;
  travelerErrorIds?: Set<string>;
  minNameLength?: number;
  scrollToDriverLicense?: boolean;
  disabledTravelerIds?: PersonId[];
  minimumCheckInAge?: number;
  checkInDate?: Date | null;
  showDefaultAddNewTravelerAction?: boolean;
  /**
   * When provided, renders the entries section as one section per group instead of a flat list.
   * Each group has its own header (default layout or `customHeader`) and its own empty-state
   * Add-Traveler button. The flat list is preserved when this prop is omitted.
   */
  travelerGroups?: TravelerGroup[];
}

/* eslint-disable react/default-props-match-prop-types */
const defaultProps: Partial<ITravelerSelectWorkflowProps> = {
  disableDriversLicenseValidation: false,
  disabled: false,
  disallowSelect: false,
  fullScreenWithBanner: false,
  isDialog: false,
  isFlights: false,
  isMobile: false,
  readOnly: false,
  saveButtonClicked: false,
  showAdditionalInfoSection: false,
  showDriverLicenseSection: false,
  showFrequentFlyerSection: false,
  showGenderField: false,
  showNationalityField: false,
  singleTravelerWorkflow: false,
  wrapButton: false,
  requireNationality: false,
  addNewTravelerButton: false,
  noHeadings: false,
  updatedDesign: false,
  allowRowClick: false,
  travelerSelectContentOnly: false,
  userRoles: [],
  hideDelete: false,
  hideTravelerActions: false,
  checkMissingInfoCompletion: false,
  mobileTravelerRowType: "button",
  showMobilePopoverTransition: true,
  showMobileChangeCTA: true,
  bypassSelectForSingleTraveler: true,
  showNonModalContent: true,
  hideMobileContinueButton: false,
  hideBackButton: false,
  hideTravelerType: false,
  useXmodHeadings: false,
};
/* eslint-enable react/default-props-match-prop-types */

export const TravelerSelectWorkflow = ({
  buttonClassName,
  buttonMessage,
  className,
  disableDriversLicenseValidation,
  disabled,
  disallowSelect,
  editPassenger,
  fullScreenWithBanner,
  handleDeletePassenger,
  handleUpdatePassenger,
  isDialog,
  isFlights,
  isMobile,
  onContinue,
  onGoBack,
  onProgressChange,
  progress,
  readOnly,
  saveButtonClicked,
  selectedTravelerIds,
  selectionScreenHeaderElement,
  setProgress,
  setSelectedTravelerIds,
  handleSelectTraveler,
  setShowErrors,
  showAdditionalInfoSection,
  renderCustomAdditionalInfoSection,
  showDriverLicenseSection,
  showFrequentFlyerSection,
  showGenderField,
  showNationalityField,
  showErrors,
  singleTravelerWorkflow,
  titles,
  travelers,
  userInfo,
  wrapButton,
  bottomContent,
  showPassportSection,
  requirePassport,
  requireNationality,
  addNewTravelerButton,
  noHeadings,
  onMobileBack,
  updatedDesign,
  allowRowClick,
  travelerSelectContentOnly,
  onClickAddNewTraveler,
  userRoles,
  tenant,
  trackEvent,
  maxTravelerCount,
  customHeader,
  hideDelete,
  showHotelLoyaltySection,
  showGroundLoyaltySection,
  onClickDeleteTraveler,
  onClickEditTraveler,
  hideTravelerActions,
  onTravelerFormClose,
  checkMissingInfoCompletion,
  mobileTravelerRowType,
  showMobilePopoverTransition,
  showMobileChangeCTA,
  bypassSelectForSingleTraveler,
  showNonModalContent,
  hideMobileContinueButton,
  hideBackButton,
  mobileContinueButtonText,
  hideTravelerType,
  getTravelerTypeByAge,
  useXmodHeadings,
  isValidatingPassenger,
  travelerErrorIds,
  minNameLength,
  scrollToDriverLicense,
  disabledTravelerIds = [],
  minimumCheckInAge,
  checkInDate,
  showDefaultAddNewTravelerAction = true,
  travelerGroups,
}: ITravelerSelectWorkflowProps): JSX.Element => {
  const [focusedPassenger, setFocusedPassenger] = React.useState<
    IPerson | undefined
  >(editPassenger);

  const [prefillPassenger, setPrefillPassenger] = React.useState<
    UserInfo | undefined
  >();

  const [travelerToDelete, setTravelerToDelete] = React.useState<
    IPerson | undefined
  >();

  const [openTravelerFormModal, setOpenTravelerFormModal] =
    React.useState<boolean>(false);
  const [openTravelerSelectModal, setOpenTravelerSelectModal] =
    React.useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = React.useState<boolean>(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const [closeFormTrigger, setCloseFormTrigger] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

  const corpTravelerDeleteConfirmationCancel = "Cancel";
  const corpTravelerDeleteConfirmationConfirm = "Yes, delete profile";
  const corpTravelerDeleteConfirmationTitle = "Delete traveler profile?";
  const isAdmin = userRoles.includes("Admin");

  React.useEffect(() => {
    setFocusedPassenger(editPassenger);
  }, [editPassenger]);

  React.useEffect(() => {
    if (setShowErrors && saveButtonClicked) {
      if (showErrors?.travelerSelect && selectedTravelerIds?.length > 0) {
        setShowErrors({
          phone: showErrors?.phone,
          email: showErrors?.email,
          travelerSelect: false,
        });
      } else if (
        !showErrors?.travelerSelect &&
        selectedTravelerIds?.length === 0
      ) {
        setShowErrors({
          phone: showErrors?.phone,
          email: showErrors?.email,
          travelerSelect: true,
        });
      }
    }
  }, [selectedTravelerIds]);

  const handleSetProgress = (step: TravelerSelectStep) => {
    setProgress(step);
    if (onProgressChange) {
      onProgressChange(step);
    }
  };

  const handleCloseForm = () => {
    handleSetProgress(TravelerSelectStep.Main);
    if (onGoBack && !isDialog) onGoBack();
  };

  const handleHasUnsavedChanges = (unsavedChanges: boolean) => {
    setCloseFormTrigger(false);
    if (unsavedChanges) {
      setShowUnsavedChangesModal(unsavedChanges);
    } else {
      handleCloseForm();
    }
  };

  const openTravelerInfo = (traveler: IPerson) => {
    if (onClickEditTraveler) {
      onClickEditTraveler(traveler);
    } else {
      setFocusedPassenger(traveler);
      handleSetProgress(TravelerSelectStep.TravelerInfoForm);
    }
  };

  const handleOpenDeleteModal = (traveler: IPerson) => {
    setTravelerToDelete(traveler);
    handleSetProgress(TravelerSelectStep.TravelerDeleteModal);
    onClickDeleteTraveler?.(traveler);
  };

  const handleClickRemove = (travelerId: PersonId) => {
    if (travelerId === "") {
      return;
    }
    if (!showDeleteConfirmationModal) {
      setFocusedPassenger(
        travelers.find((traveler) => traveler.id === travelerId),
      );
      trackEvent({
        eventName: MODAL_ALERT,
        properties: {
          type: "delete_profile_confirmation",
          category: ModalCategoryType.FEATURE,
          primary_button: "yes, delete profile",
          secondary_button: "cancel",
        },
      });
      setShowDeleteConfirmationModal(true);
      return;
    }
    handleDeletePassenger(travelerId);
  };

  const handleOnDeleteModalClose = () => {
    handleSetProgress(
      isMobile ? TravelerSelectStep.TravelerSelect : TravelerSelectStep.Main,
    );
    setTravelerToDelete(undefined);
  };

  const handleDeleteModalRemove = (travelerId: PersonId) => {
    handleDeletePassenger(travelerId);
    handleOnDeleteModalClose();
  };

  const handleClickSelect = (travelerId: PersonId) => {
    // hacky way to show edit form pop if driver has no license
    if (showDriverLicenseSection || checkMissingInfoCompletion) {
      const traveler = travelers.find((t) => t.id === travelerId);
      const isTravelerMissingInfo =
        traveler && "isMissingInfo" in traveler
          ? traveler.isMissingInfo
          : false;
      if (showDriverLicenseSection && !traveler?.driverLicense?.street) {
        openTravelerInfo(traveler as IPerson);
        return;
      }
      if (isTravelerMissingInfo) {
        openTravelerInfo(traveler as IPerson);
        return;
      }
    }

    // Hacky way to ensure user can only select a single traveler.
    if (singleTravelerWorkflow) {
      setSelectedTravelerIds([travelerId]);
      handleSelectTraveler?.(travelerId);
      return;
    }

    let newTravelerIds = [...selectedTravelerIds];

    if (newTravelerIds.includes(travelerId)) {
      newTravelerIds = newTravelerIds.filter((id) => id !== travelerId);
    } else {
      newTravelerIds.push(travelerId);
    }

    setSelectedTravelerIds(newTravelerIds);
    handleSelectTraveler?.(travelerId);
  };

  const handlePrefillPassenger = () => {
    if (travelers.length === 0) setPrefillPassenger(userInfo);
    else setPrefillPassenger(undefined);
  };

  const handleAddTraveler = () => {
    setFocusedPassenger(undefined);
    handleSetProgress(TravelerSelectStep.TravelerInfoForm);
    handlePrefillPassenger();
    onClickAddNewTraveler?.();
  };

  const getTravelerType = (traveler: IPerson) => {
    if (hideTravelerType) return undefined;

    const age = dayjs().diff(
      getDateTimeWithFormat(traveler.dateOfBirth, "YYYY-MM-DD"),
      "year",
    );

    if (getTravelerTypeByAge) {
      return getTravelerTypeByAge(age);
    }

    if (age > 11) {
      return titles.adultTitle;
    }

    if (age >= 2) {
      return titles.childTitle;
    }

    if (age > 1) {
      return titles.infantSeatTitle;
    }

    return titles.infantLapTitle;
  };

  const renderTravelerEntriesSection = ({
    showRemoveButton,
    showSelectButton,
    showSelectedEntriesOnly,
  }: {
    showRemoveButton?: boolean;
    showSelectButton?: boolean;
    showSelectedEntriesOnly?: boolean;
  }) => {
    const renderTravelerRow = (
      traveler: IPerson | ICorpPerson,
      forceDisallowSelectAndGrayOut = false,
    ) => {
      const classNames =
        !isFlights &&
        !isMobile &&
        selectedTravelerIds?.length > 0 &&
        !selectedTravelerIds.includes(traveler.id)
          ? "grey"
          : "";
      const travelerName = `${traveler.givenName} ${traveler.surname}`;
      const isDefaultProfile =
        "isDefaultProfile" in traveler ? traveler.isDefaultProfile : false;
      const isDEUser =
        "isGhostUser" in traveler ? !traveler.isGhostUser : false;
      const isMissingInfo =
        "isMissingInfo" in traveler ? traveler.isMissingInfo : false;
      const isTravelerProfilePrivate = isCorpTenant(tenant)
        ? (traveler as ICorpPersonWithNameNumber).isPrivateProfile
        : false;

      const travelerRowKey = traveler.id;

      const onClickRemove = () =>
        updatedDesign
          ? handleOpenDeleteModal(traveler)
          : handleClickRemove(traveler.id);

      if (!isMobile || mobileTravelerRowType === "checkbox") {
        // Desktop view
        return (
          <TravelerSelectRowUpdated
            buttonClassName={buttonClassName}
            className={clsx(`traveler-select-workflow-entry-root`, classNames, {
              "mobile-checkbox": isMobile,
            })}
            disabled={disabled}
            disallowSelect={disallowSelect}
            disallowSelectAndGrayOut={
              disabledTravelerIds.includes(traveler.id) ||
              forceDisallowSelectAndGrayOut
            }
            fullName={travelerName}
            isFlights={isFlights}
            // isMobile={isMobile}
            isSelected={selectedTravelerIds.includes(traveler.id)}
            key={travelerRowKey}
            onClickEdit={
              hideTravelerActions ? undefined : () => openTravelerInfo(traveler)
            }
            onClickRemove={showRemoveButton ? onClickRemove : undefined}
            onClickSelect={
              showSelectButton
                ? () => handleClickSelect(traveler.id)
                : undefined
            }
            onClickView={
              hideTravelerActions ? undefined : () => openTravelerInfo(traveler)
            }
            onClickRow={() => {
              if (allowRowClick) {
                openTravelerInfo(traveler);
              }
            }}
            readOnly={readOnly}
            travelerType={getTravelerType(traveler)}
            wrapButton={wrapButton}
            isDefaultProfile={isDefaultProfile}
            isDEUser={isDEUser}
            isMissingInfo={isMissingInfo}
            isTravelerProfilePrivate={isTravelerProfilePrivate}
            hasTravelerError={travelerErrorIds?.has(traveler.id) ?? false}
          />
        );
      }

      const isMaxTravelersSelected =
        maxTravelerCount !== undefined &&
        selectedTravelerIds.length >= maxTravelerCount;
      const isTravelerRowDisabled =
        isMaxTravelersSelected && !selectedTravelerIds.includes(traveler.id);

      // Mobile view
      return (
        <TravelerSelectRow
          buttonClassName={buttonClassName}
          className="traveler-select-workflow-entry-root"
          disabled={disabled || isTravelerRowDisabled}
          disallowSelect={disallowSelect}
          disallowSelectAndGrayOut={disabledTravelerIds.includes(traveler.id)}
          fullName={travelerName}
          isMobile={isMobile}
          isSelected={selectedTravelerIds.includes(traveler.id)}
          key={travelerRowKey}
          onClickEdit={() => openTravelerInfo(traveler)}
          onClickRemove={showRemoveButton ? onClickRemove : undefined}
          isValidatingPassenger={
            isValidatingPassenger ? isValidatingPassenger(traveler.id) : false
          }
          onClickSelect={
            showSelectButton ? () => handleClickSelect(traveler.id) : undefined
          }
          onClickView={() => openTravelerInfo(traveler)}
          onClickRow={() => {
            if (allowRowClick) {
              openTravelerInfo(traveler);
            }
          }}
          readOnly={readOnly}
          travelerType={getTravelerType(traveler)}
          wrapButton={wrapButton}
          hasTravelerError={travelerErrorIds?.has(traveler.id) ?? false}
        />
      );
    };

    const renderGroupAddTravelerButton = () => {
      if (disabled || readOnly) {
        return null;
      }
      return (
        <ActionButton
          className="traveler-select-group-workflow-button add-traveler"
          message={buttonMessage || "Add new traveler"}
          disabled={disabled}
          onClick={handleAddTraveler}
          defaultStyle="h4r-secondary"
        />
      );
    };

    if (travelerGroups) {
      return (
        <Box className="traveler-select-workflow-entries-section traveler-grouped-entries-section">
          {travelerGroups.map((group) => {
            const groupTravelers = travelers.filter((traveler) =>
              group.travelerIds.includes(traveler.id),
            );
            const selectedCount = group.travelerIds.filter((id) =>
              selectedTravelerIds.includes(id),
            ).length;
            const hasEnough = selectedCount >= group.required;

            return (
              <Box key={group.id} className="traveler-group-section">
                <Box className="traveler-group-header">
                  <Box className="traveler-group-header-left">
                    <Typography
                      variant="h5"
                      className={clsx("traveler-group-label", {
                        "traveler-group-label-error":
                          !hasEnough && showErrors?.travelerSelect,
                      })}
                    >
                      <strong>{group.label}</strong>
                    </Typography>
                    {group.sublabel && (
                      <Typography className="traveler-group-sublabel">
                        {group.sublabel}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant="h5"
                    className={clsx("traveler-group-selected-count", {
                      "traveler-group-selected-count-error":
                        !hasEnough && showErrors?.travelerSelect,
                      "traveler-group-selected-count-warning":
                        !hasEnough && !showErrors?.travelerSelect,
                      "traveler-group-selected-count-met": hasEnough,
                    })}
                  >
                    {selectedCount} / {group.required} selected
                  </Typography>
                </Box>

                {groupTravelers.length === 0 ? (
                  <Box className="traveler-group-empty">
                    {titles.noTravelersTitle && (
                      <Typography
                        className="no-travelers-heading"
                        variant="body2"
                      >
                        {titles.noTravelersTitle}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box className="traveler-group-entries">
                    {groupTravelers.map((traveler) =>
                      renderTravelerRow(
                        traveler,
                        hasEnough && !selectedTravelerIds.includes(traveler.id),
                      ),
                    )}
                  </Box>
                )}
                {(group.isLast || groupTravelers.length === 0) &&
                  renderGroupAddTravelerButton()}
              </Box>
            );
          })}
        </Box>
      );
    }

    // On mobile, the main page should only show selected passengers
    const filteredTravelers =
      !travelerSelectContentOnly && showSelectedEntriesOnly
        ? travelers.filter((traveler) =>
            selectedTravelerIds.includes(traveler.id),
          )
        : travelers;
    if (filteredTravelers.length === 0 && titles.noTravelersTitle) {
      if (isMobile && travelerSelectContentOnly)
        return (
          <Box className="no-travelers-container">
            <Icon name={IconName.UserAdd} />
            <Typography className="no-travelers-heading" variant="h6">
              {titles.noTravelersTitle}
            </Typography>
          </Box>
        );

      return (
        <Typography className="no-travelers-heading" variant="h6">
          {titles.noTravelersTitle}
        </Typography>
      );
    }
    return (
      <Box className="traveler-select-workflow-entries-section">
        {filteredTravelers.map((traveler) => renderTravelerRow(traveler))}
      </Box>
    );
  };

  const handleOnClose = () => {
    setCloseFormTrigger(true);
    handleCloseForm();
    onTravelerFormClose?.();
  };

  // This is currently a mobile only popup
  const renderTravelerEntriesSectionPopup = () => {
    const isContinueDisabled =
      selectedTravelerIds.length === 0 || hideMobileContinueButton;

    const renderBackButton = () => (
      <BackButton
        className="traveler-entries-select-popup-back-button"
        onClick={onMobileBack || handleOnClose}
      />
    );

    const renderContent = () => (
      <Box
        className={clsx("traveler-entries-select-container", {
          "continue-disabled": isContinueDisabled,
          xmod: useXmodHeadings,
        })}
      >
        {isMobile && customHeader}
        {renderTravelerEntriesSection({
          showRemoveButton: !hideDelete,
          showSelectButton: true,
        })}
        {!readOnly && showDefaultAddNewTravelerAction && (
          <Box className="traveler-entries-select-buttons-container">
            <ActionButton
              className={clsx(
                "traveler-select-workflow-button",
                "add-traveler",
              )}
              message={buttonMessage || "Add new traveler"}
              disabled={disabled}
              onClick={handleAddTraveler}
              defaultStyle="h4r-secondary"
            />
          </Box>
        )}
        {bottomContent}
        {!isContinueDisabled && (
          <MobileFloatingButton
            className="traveler-entries-continue-button"
            wrapperClassName="traveler-entries-continue-button-container"
            onClick={() => {
              if (onContinue) onContinue();
              handleSetProgress(TravelerSelectStep.Main);
            }}
            dataTestId="select-traveler-continue-button"
          >
            {mobileContinueButtonText || "Continue"}
          </MobileFloatingButton>
        )}
      </Box>
    );

    if (travelerSelectContentOnly) return renderContent();

    return (
      <MobilePopoverCard
        open={openTravelerSelectModal}
        className={clsx(
          "local-traveler-entries-select-popup",
          "mobile",
          className,
          {
            "updated-design": updatedDesign,
          },
        )}
        onClose={onMobileBack || handleOnClose}
        fullScreen
        fullScreenWithBanner={fullScreenWithBanner}
        topLeftButton={!hideBackButton ? renderBackButton() : undefined}
        headerElement={selectionScreenHeaderElement}
        showTransition={showMobilePopoverTransition}
      >
        {renderContent()}
      </MobilePopoverCard>
    );
  };

  const renderTravelerInfoFormPopup = () => {
    const isTravelerProfilePrivate =
      focusedPassenger && "isPrivateProfile" in focusedPassenger
        ? (focusedPassenger as ICorpPerson).isPrivateProfile
        : false;
    const renderContent = (closeButton?: JSX.Element) => (
      <>
        <TravelerInfoForm
          additionalInfoTitle={titles.additionalInfoTitle}
          buttonClassName={buttonClassName}
          className="traveler-select-workflow-info-form"
          closeButton={closeButton}
          disableDriversLicenseValidation={disableDriversLicenseValidation}
          frequentFlyerTitle={titles.frequentFlyerTitle}
          handleUpdatePassenger={(
            traveler: IPerson,
            hideProfileAction: ToggleActions,
            entryPoint?: string,
            updatedPassport?: boolean,
            isNewTraveler?: boolean,
          ) => {
            handleUpdatePassenger(
              traveler,
              hideProfileAction,
              entryPoint,
              updatedPassport,
              isNewTraveler,
            );
            if (isMobile) {
              handleSetProgress(TravelerSelectStep.TravelerSelect);
            } else {
              handleSetProgress(TravelerSelectStep.Main);
            }
          }}
          hideHeader={!isMobile}
          isMobile={isMobile}
          prefillPassenger={prefillPassenger}
          readOnly={readOnly}
          showAdditionalInfoSection={showAdditionalInfoSection}
          renderCustomAdditionalInfoSection={renderCustomAdditionalInfoSection}
          showDriverLicenseSection={showDriverLicenseSection}
          showFrequentFlyerSection={showFrequentFlyerSection}
          showGenderField={showGenderField}
          showNationalityField={showNationalityField}
          traveler={focusedPassenger}
          travelerInfoSubtitle={
            focusedPassenger
              ? titles.editTravelerSubtitle || titles.travelerInfoFormSubtitle
              : titles.travelerInfoFormSubtitle || titles.travelerInfoSubtitle
          }
          travelerInfoTitle={
            (focusedPassenger
              ? titles.editTravelerTitle || titles.addTravelers
              : titles.addTravelers) || titles.travelerInfoTitle
          }
          showPassportSection={showPassportSection}
          requirePassport={requirePassport}
          frequentFlyerSubtitle={titles.frequentFlyerSubtitle}
          travelerInfoSectionTitle={titles.travelerInfoSectionTitle}
          passportTitle={titles.passportTitle}
          passportSubtitle={titles.passportSubtitle}
          passportSubtitle2={titles.passportSubtitle2}
          requireNationality={requireNationality}
          completeTravelerProfileTitle={titles.completeTravelerProfileTitle}
          tenant={tenant}
          isTravelerProfilePrivate={isTravelerProfilePrivate}
          showContactEmailSection={isCorpTenant(tenant)}
          isUserAdmin={userRoles.includes(CORPORATE_ROLE_ADMIN)}
          handleUnsavedChanges={handleHasUnsavedChanges}
          closeFormTrigger={closeFormTrigger}
          showHotelLoyaltySection={showHotelLoyaltySection}
          showGroundLoyaltySection={showGroundLoyaltySection}
          minNameLength={minNameLength}
          scrollToDriverLicense={scrollToDriverLicense}
          minimumCheckInAge={minimumCheckInAge}
          checkInDate={checkInDate}
        />
        {isCorpTenant(tenant) && (
          <UnsavedChangesModal
            open={showUnsavedChangesModal}
            onClose={() => {
              setShowUnsavedChangesModal(false);
            }}
            onConfirm={() => {
              setShowUnsavedChangesModal(false);
              handleCloseForm();
            }}
            onCancel={() => {
              setShowUnsavedChangesModal(false);
            }}
          />
        )}
      </>
    );

    const renderCloseButton = () => (
      <ActionLink
        showTappableArea
        className="traveler-form-popup-close-button"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.stopPropagation();
            handleSetProgress(
              isMobile
                ? TravelerSelectStep.TravelerSelect
                : TravelerSelectStep.Main,
            );
            onTravelerFormClose?.();
          }
        }}
        onClick={(ev: MouseEvent) => {
          ev.stopPropagation();
          handleSetProgress(
            isMobile
              ? TravelerSelectStep.TravelerSelect
              : TravelerSelectStep.Main,
          );
          onTravelerFormClose?.();
        }}
        content={<CloseButtonIcon className="close-button-icon" />}
        label="Close"
      />
    );

    const isMissingInfo =
      focusedPassenger && "isMissingInfo" in focusedPassenger
        ? (focusedPassenger as ICorpPerson).isMissingInfo
        : false;

    const titleToUse = focusedPassenger
      ? titles.editTravelerTitle
      : titles.addTravelers;

    return (
      <>
        {!isMobile && (
          <DesktopPopupModal
            invisibleBackdrop={false}
            open={openTravelerFormModal}
            className="traveler-select-workflow-info-form-popup"
            headerElement={
              <Box className="traveler-info-header">
                <Typography variant="h6" component="h2">
                  {isMissingInfo
                    ? titles.completeTravelerProfileTitle
                    : titleToUse}
                </Typography>
                <Typography variant="body2">
                  {isMissingInfo
                    ? titles.completeTravelerProfileSubtitle
                    : titles.travelerInfoFormSubtitle ||
                      titles.travelerInfoSubtitle}
                </Typography>
              </Box>
            }
            onClose={handleOnClose}
          >
            {renderContent(renderCloseButton())}
          </DesktopPopupModal>
        )}
        {isMobile && (
          <MobilePopoverCard
            fullScreen
            open={openTravelerFormModal}
            className={clsx(
              "traveler-select-workflow-info-form-popup",
              "mobile",
              { "updated-design": updatedDesign },
            )}
            onClose={handleOnClose}
            topRightButton={renderCloseButton()}
            fullScreenWithBanner={fullScreenWithBanner}
          >
            {renderContent(renderCloseButton())}
          </MobilePopoverCard>
        )}
      </>
    );
  };

  const renderDeleteModal = () =>
    travelerToDelete && (
      <GenericInfoPopup
        showCloseButton
        onClose={handleOnDeleteModalClose}
        buttons={[
          {
            buttonText: "Delete",
            buttonWrapperClassName: clsx(
              "traveler-delete-modal-cta",
              "traveler-delete-modal-confirm",
            ),
            defaultStyle: "h4r-primary",
            onClick: () => handleDeleteModalRemove(travelerToDelete.id),
            fill: "red",
          },
          {
            buttonText: "Cancel",
            buttonWrapperClassName: clsx(
              "traveler-delete-modal-cta",
              "traveler-delete-modal-cancel",
            ),
            defaultStyle: "h4r-secondary",
            onClick: () => handleOnDeleteModalClose(),
            fill: "no-fill",
          },
        ]}
        className={clsx("local-traveler-delete-modal-popup", {
          mobile: isMobile,
        })}
        image={<Icon className="error-icon" name={IconName.UserSubtract} />}
        isMobile={isMobile}
        open={openDeleteModal || !!travelerToDelete}
        subtitle={
          <Typography className="traveler-delete-modal-text" variant="h3">
            {"Are you sure you want to delete "}
            <strong>{getPassengerName(travelerToDelete)}</strong>
            {" from your saved travelers?"}
          </Typography>
        }
      />
    );

  React.useEffect(() => {
    if (progress === TravelerSelectStep.Main) {
      setOpenTravelerFormModal(false);
      setOpenTravelerSelectModal(false);
      setOpenDeleteModal(false);
    } else if (
      progress === TravelerSelectStep.TravelerInfoForm ||
      (bypassSelectForSingleTraveler && readOnly && travelers.length === 1) // bypass select if only 1 traveler
    ) {
      setOpenTravelerFormModal(true);
      setOpenTravelerSelectModal(false);
      setOpenDeleteModal(false);
    } else if (progress === TravelerSelectStep.TravelerSelect) {
      setOpenTravelerFormModal(false);
      setOpenTravelerSelectModal(true);
      setOpenDeleteModal(false);
    } else if (progress === TravelerSelectStep.TravelerDeleteModal) {
      setOpenTravelerFormModal(false);
      setOpenTravelerSelectModal(isMobile);
      setOpenDeleteModal(true);
    }
  }, [progress]);

  return (
    <>
      {showNonModalContent && !travelerSelectContentOnly && (
        <Box
          className={clsx(
            "local-traveler-select-workflow-root",
            { mobile: isMobile },
            className,
          )}
          data-dd-action-name="Choose traveler"
        >
          <Box className="traveler-select-workflow-container">
            {!noHeadings &&
              !travelerGroups &&
              (customHeader || (
                <Box className="traveler-select-workflow-description">
                  <Typography variant="h4" component="h3" className="title">
                    {titles.travelerInfoTitle}
                  </Typography>
                  <Typography variant="body2" className="subtitle">
                    {titles.travelerInfoSubtitle}
                  </Typography>
                  {isDialog && (
                    <ActionLink
                      className="traveler-details-close-btn"
                      content={<CloseButtonIcon />}
                      onClick={onGoBack}
                    />
                  )}
                </Box>
              ))}

            {renderTravelerEntriesSection({
              showRemoveButton: !isMobile,
              showSelectButton: !isMobile,
              showSelectedEntriesOnly: isMobile,
            })}
            {!readOnly && (
              <Box className="traveler-select-workflow-buttons-section">
                {!disabled && travelers?.length === 0 && (
                  <ActionButton
                    className="traveler-select-workflow-button add-traveler"
                    message={
                      buttonMessage ||
                      (!isMobile ? "Add traveler" : "Add new traveler")
                    }
                    disabled={disabled}
                    onClick={handleAddTraveler}
                    defaultStyle={
                      addNewTravelerButton ? "h4r-primary" : "h4r-secondary"
                    }
                  />
                )}
                {!disabled &&
                  travelers?.length > 0 &&
                  !isMobile &&
                  showDefaultAddNewTravelerAction &&
                  (addNewTravelerButton ? (
                    <ActionButton
                      className="b2b add-traveler traveler-select-workflow-button"
                      onClick={handleAddTraveler}
                      message={
                        buttonMessage ||
                        (!isMobile ? "Add traveler" : "Add new traveler")
                      }
                      defaultStyle="h4r-primary"
                    />
                  ) : (
                    <ActionLink
                      className="b2b add-traveler traveler-select-workflow-link"
                      onClick={handleAddTraveler}
                      onKeyDown={handleAddTraveler}
                      content={buttonMessage || "Add new traveler"}
                      label={
                        buttonMessage ||
                        (!isMobile ? "Add traveler" : "Add new traveler")
                      }
                      showTappableArea={false}
                    />
                  ))}
                {isMobile && showMobileChangeCTA && (
                  <ActionButton
                    className="traveler-select-workflow-button change"
                    message="Change"
                    disabled={disabled}
                    onClick={() =>
                      handleSetProgress(TravelerSelectStep.TravelerSelect)
                    }
                    defaultStyle="h4r-secondary"
                  />
                )}
              </Box>
            )}
            <CorpGenericConfirmationModalContent
              isOpen={showDeleteConfirmationModal}
              onClose={() => {
                setShowDeleteConfirmationModal(false);
              }}
              title={corpTravelerDeleteConfirmationTitle}
              titleImage={<Icon name={IconName.CautionTriangle} />}
              description={
                <Typography
                  variant="subtitle1"
                  style={{
                    fontSize: "20px",
                    marginTop: "20px",
                    lineHeight: "1.25",
                    fontWeight: 300,
                  }}
                >
                  Are you sure you want to delete{" "}
                  <strong>
                    {focusedPassenger?.givenName} {focusedPassenger?.surname}
                  </strong>
                  ? This will remove the traveler information{" "}
                  {isAdmin ? "for all admins" : ""} and cannot be undone.
                </Typography>
              }
              onConfirm={() => {
                trackEvent({
                  eventName: MODAL_ALERT_CHOICE,
                  properties: {
                    type: "delete_profile_confirmation",
                    category: ModalCategoryType.FEATURE,
                    primary_button: "yes, delete profile",
                    secondary_button: "cancel",
                    button_choice: ModalButtonType.PRIMARY,
                  },
                });
                setShowDeleteConfirmationModal(false);
                handleClickRemove(focusedPassenger?.id ?? "");
              }}
              onCancel={() => {
                trackEvent({
                  eventName: MODAL_ALERT_CHOICE,
                  properties: {
                    type: "delete_profile_confirmation",
                    category: ModalCategoryType.FEATURE,
                    primary_button: "yes, delete profile",
                    secondary_button: "cancel",
                    button_choice: ModalButtonType.SECONDARY,
                  },
                });
                setShowDeleteConfirmationModal(false);
              }}
              cancelCtaLabel={corpTravelerDeleteConfirmationCancel}
              confirmCtaLabel={corpTravelerDeleteConfirmationConfirm}
              confirmCtaColor="var(--button-critical-background)"
              cancelCtaColor="#013D5B"
            />
          </Box>
        </Box>
      )}
      {renderTravelerInfoFormPopup()}
      {renderTravelerEntriesSectionPopup()}
      {renderDeleteModal()}
    </>
  );
};

TravelerSelectWorkflow.defaultProps = defaultProps;
